import * as Sentry from '@sentry/nextjs'
import { jwtDecode } from 'jwt-decode'

import { REFRESH_TOKEN_KEY } from '@/legacy-src/constants/storage'
import type { ApisType } from '@/legacy-src/store/createApis'
import handleLogout from '@/legacy-src/utilities/handleLogout'
import { setTokens } from '@/legacy-src/utilities/setTokens'

// Refresh slightly before the real expiry so a token that lapses in-flight
// doesn't cause an avoidable 401.
const EXPIRY_SKEW_SECONDS = 10

let inFlightRefresh: Promise<string | null> | null = null

const isTokenExpired = (bearer: unknown): boolean => {
  if (typeof bearer !== 'string' || !bearer.startsWith('Bearer ')) {
    return true
  }
  try {
    const { exp } = jwtDecode(bearer.slice('Bearer '.length))
    return !exp || exp <= Date.now() / 1000 + EXPIRY_SKEW_SECONDS
  } catch {
    return true
  }
}

const requestFreshAccessToken = async (api: ApisType['api']): Promise<string | null> => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
  if (!refreshToken || isTokenExpired(`Bearer ${refreshToken}`)) {
    handleLogout()
    return null
  }

  try {
    const response = await api.refreshToken(refreshToken)
    if (response.status !== 200) {
      handleLogout()
      return null
    }
    setTokens({ api, data: response.data })
    return response.data.accessToken
  } catch (error) {
    Sentry.captureException(error, { tags: { type: 'octopus-token-refresh' } })
    handleLogout()
    return null
  }
}

/**
 * Refreshes the access token from the refresh token in localStorage and returns
 * the new one, or null when the session can't be refreshed (in which case the
 * user is logged out). Persists the rotated tokens via `setTokens` and dedupes
 * concurrent callers so the single-use refresh token isn't raced.
 *
 * The axios `api` instance refreshes on 401 via a response interceptor; octopus
 * calls use native `fetch` and bypass it, so they refresh through here.
 */
export const refreshAccessToken = (api: ApisType['api']): Promise<string | null> => {
  inFlightRefresh ??= requestFreshAccessToken(api).finally(() => {
    inFlightRefresh = null
  })
  return inFlightRefresh
}

/**
 * Returns a valid `Bearer <token>` for an octopus call, refreshing proactively
 * when the current access token has expired rather than waiting for a 401.
 * Returns null when the session can't be refreshed (user is logged out).
 */
export const getValidAccessToken = async (api: ApisType['api']): Promise<string | null> => {
  const current = api.headers.Authorization
  if (!isTokenExpired(current)) {
    return current as string
  }

  const accessToken = await refreshAccessToken(api)
  return accessToken ? `Bearer ${accessToken}` : null
}
